import { HomeFeedType, WebClientPreference } from '@ms/yammer-graphql';
import { Reducer } from 'redux';

import { getGraphqlApiErrorReason } from '../../..';
import { GraphqlErrorReason } from '../../../api/types';
import { UpdateHomeFeedPreferenceAction } from '../../../domains/sessionHomeFeedPreference/actions.types';
import {
  SessionLoadCurrentUserAction,
  SessionLoadCurrentUserFulfilledAction,
  SessionLoadCurrentUserRejectedAction,
} from '../../../domains/sessionLoadCurrentUser/actions.types';
import {
  UpdateUserPreferencesAction,
  UpdateUserPreferencesFulfilledAction,
} from '../../../domains/userPreferences/actions.types';

import { CurrentUser } from './types';

export type UserAction = SessionLoadCurrentUserAction | UpdateUserPreferencesAction | UpdateHomeFeedPreferenceAction;

const initialState: CurrentUser = {
  id: '',
  hasLicense: true,
  isGuest: true,
  isPending: false,
  isNetworkAdmin: false,
  isVerifiedNetworkAdmin: false,
  userPreferences: { webClientPreference: WebClientPreference.NONE },
  isUserForceOptedIn: false,
  homeFeedPreference: HomeFeedType.DISCOVERY,
  canScheduleAndCreateTeamsMeeting: false,
  canSendOnBehalfOf: false,
  directMessageThreadStarterSmallFileUploadUrl: '',
};

const checkForLicenseError = (state: CurrentUser, action: SessionLoadCurrentUserRejectedAction) => {
  if (getGraphqlApiErrorReason(action.payload.error) === GraphqlErrorReason.NoYammerLicense) {
    return { ...state, hasLicense: false };
  }

  return state;
};

const checkForPendingUserError = (state: CurrentUser, action: SessionLoadCurrentUserRejectedAction) => {
  if (getGraphqlApiErrorReason(action.payload.error) === GraphqlErrorReason.PendingUser) {
    return { ...state, isPending: true };
  }

  return state;
};

const checkForErrors = (state: CurrentUser, action: SessionLoadCurrentUserRejectedAction) => {
  const { error } = action.payload;

  return { ...state, error };
};

const stateWithAdmins = (state: CurrentUser, action: SessionLoadCurrentUserFulfilledAction) => {
  const {
    isNetworkAdmin,
    isGuest,
    isVerifiedNetworkAdmin,
    id,
    userPreferences,
    isUserForceOptedIn,
    canScheduleAndCreateTeamsMeeting,
    canSendOnBehalfOf,
    directMessageThreadStarterSmallFileUploadUrl,
  } = action.payload;

  return {
    ...state,
    id,
    isGuest,
    isNetworkAdmin,
    isVerifiedNetworkAdmin,
    userPreferences,
    isUserForceOptedIn,
    canScheduleAndCreateTeamsMeeting,
    canSendOnBehalfOf,
    directMessageThreadStarterSmallFileUploadUrl,
  };
};

const stateWithUpdatedUserPreferences = (state: CurrentUser, action: UpdateUserPreferencesFulfilledAction) => ({
  ...state,
  userPreferences: {
    ...state.userPreferences,
    webClientPreference: action.payload,
  },
});

const stateWithUpdatedHomeFeedPreference = (state: CurrentUser, action: UpdateHomeFeedPreferenceAction) => ({
  ...state,
  ...action.payload,
});

const reducer: Reducer<CurrentUser, UserAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'SESSION.LOAD_CURRENT_USER_FULFILLED':
      return stateWithAdmins(state, action);
    case 'SESSION.LOAD_CURRENT_USER_REJECTED': {
      const stateWithLicense = checkForLicenseError(state, action);
      const statewithPendingUser = checkForPendingUserError(stateWithLicense, action);

      return checkForErrors(statewithPendingUser, action);
    }
    case 'USER_PREFERENCES.UPDATE_USER_PREFERENCES_FULFILLED':
      return stateWithUpdatedUserPreferences(state, action);
    case 'SESSION.UPDATE_HOME_FEED_PREFERENCE':
      return stateWithUpdatedHomeFeedPreference(state, action);
    default:
      return state;
  }
};

export default reducer;
